import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  MessageCircle, Loader2, Send, Clock, CheckCircle,
  XCircle, User, Calendar, Headphones, RefreshCw, X,
} from 'lucide-react';
import { adminFetch } from '../lib/api';
import * as Sentry from '@sentry/react';

interface SupportChat {
  id: string;
  user_id: string;
  user_email: string;
  title: string;
  status: 'open' | 'closed';
  unread_user: number;
  unread_admin: number;
  created_at: string;
  updated_at: string;
}

interface SupportMessage {
  id: string;
  chat_id: string;
  sender: 'user' | 'admin';
  sender_email: string;
  text: string;
  created_at: string;
}

const STATUS_CONFIG = {
  open: { label: 'Offen', badge: 'badge-warning', icon: <Clock size={12} /> },
  closed: { label: 'Geschlossen', badge: 'badge-success', icon: <CheckCircle size={12} /> },
};

export const SupportChatPage: React.FC = () => {
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState<SupportChat | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load all chats via dedicated admin API ──────────────────────
  const loadChats = useCallback(async () => {
    try {
      const res = await adminFetch('/api/support-chat/admin/all');
      if (res.ok) {
        const data = await res.json();
        setChats(data.chats || []);
      }
    } catch (err) {
      console.error('[AdminChat] loadChats failed:', err);
      Sentry.captureException(err, { tags: { module: 'admin-chat', action: 'loadChats' } });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load messages via dedicated admin API ───────────────────────
  const loadMessages = useCallback(async (chatId: string) => {
    setLoadingMessages(true);
    try {
      const res = await adminFetch(`/api/support-chat/admin/messages/${chatId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        // Server already marks unread_admin = 0, update local state
        setChats(prev => prev.map(c => c.id === chatId ? { ...c, unread_admin: 0 } : c));
      }
    } catch (err) {
      console.error('[AdminChat] loadMessages failed:', err);
      Sentry.captureException(err, { tags: { module: 'admin-chat', action: 'loadMessages' } });
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  // Auto-scroll messages
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Poll for new messages every 10s
  useEffect(() => {
    if (selectedChat) {
      pollRef.current = setInterval(() => {
        loadMessages(selectedChat.id);
        loadChats();
      }, 10000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedChat, loadMessages, loadChats]);

  // ── Admin reply via dedicated API ───────────────────────────────
  const handleReply = async () => {
    if (!selectedChat || !replyText.trim() || sending) return;
    setSending(true);
    setReplyError(null);

    try {
      const res = await adminFetch('/api/support-chat/admin/reply', {
        method: 'POST',
        body: JSON.stringify({ chatId: selectedChat.id, text: replyText.trim() }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
        setReplyText('');
        setReplyError(null);
        loadChats(); // Refresh list to update timestamps
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error('[AdminChat] Reply failed:', errData.error);
        setReplyError(errData.error || 'Antwort konnte nicht gesendet werden.');
      }
    } catch (err) {
      console.error('[AdminChat] Reply error:', err);
      Sentry.captureException(err, { tags: { module: 'admin-chat', action: 'reply' } });
      setReplyError('Netzwerkfehler — bitte erneut versuchen.');
    }

    setSending(false);
  };

  // ── Status change via dedicated API ─────────────────────────────
  const handleStatusChange = async (chat: SupportChat, newStatus: 'open' | 'closed') => {
    try {
      const res = await adminFetch(`/api/support-chat/admin/status/${chat.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });

      if (res.ok) {
        setSelectedChat(prev => prev ? { ...prev, status: newStatus } : prev);
        loadChats();
      }
    } catch (err) {
      console.error('[AdminChat] Status change failed:', err);
      Sentry.captureException(err, { tags: { module: 'admin-chat', action: 'statusChange' } });
    }
  };

  const filtered = React.useMemo(() => {
    let result = [...chats];
    if (filterStatus) result = result.filter(c => c.status === filterStatus);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(term) ||
        c.user_email.toLowerCase().includes(term)
      );
    }
    return result;
  }, [chats, filterStatus, searchTerm]);

  const openCount = chats.filter(c => c.status === 'open').length;
  const unreadCount = chats.reduce((sum, c) => sum + (c.unread_admin || 0), 0);

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><MessageCircle size={24} /> Live-Chat</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><MessageCircle size={24} /> Live-Chat</h1>
        <button className="btn btn-secondary btn-sm" onClick={() => loadChats()}>
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* KPIs */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Gesamt</span>
          <span className="kpi-value">{chats.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Offen</span>
          <span className="kpi-value" style={{ color: openCount > 0 ? 'var(--warning)' : 'var(--success)' }}>{openCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ungelesene</span>
          <span className="kpi-value" style={{ color: unreadCount > 0 ? 'var(--danger)' : 'var(--success)' }}>{unreadCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Geschlossen</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>{chats.filter(c => c.status === 'closed').length}</span>
        </div>
      </div>

      {/* Main Layout: List + Detail */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedChat ? '340px 1fr' : '1fr', gap: 16, minHeight: 500 }}>
        {/* Chat List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Filter bar */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              className="input input-search"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 120 }}
            />
            <select className="input" style={{ width: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Alle</option>
              <option value="open">Offen</option>
              <option value="closed">Geschlossen</option>
            </select>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                {chats.length === 0 ? 'Noch keine Chats.' : 'Keine Treffer.'}
              </div>
            ) : (
              filtered.map(chat => {
                const isActive = selectedChat?.id === chat.id;
                const sc = STATUS_CONFIG[chat.status];
                return (
                  <div
                    key={chat.id}
                    onClick={() => { setSelectedChat(chat); loadMessages(chat.id); }}
                    style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: isActive ? 'var(--bg-elevated)' : 'transparent',
                      borderLeft: isActive ? '3px solid var(--primary)' : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                        {chat.title}
                      </span>
                      {chat.unread_admin > 0 && (
                        <span style={{
                          background: 'var(--danger)',
                          color: '#fff',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '1px 6px',
                          minWidth: 18,
                          textAlign: 'center',
                        }}>
                          {chat.unread_admin}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                      <span className={`badge ${sc.badge}`} style={{ fontSize: 10, padding: '1px 6px' }}>{sc.icon} {sc.label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{chat.user_email}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                      {new Date(chat.updated_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Detail */}
        {selectedChat && (
          <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Chat Header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: 'var(--bg-elevated)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedChat.title}</div>
                <div className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  <User size={12} /> {selectedChat.user_email}
                  <span>·</span>
                  <Calendar size={12} /> {new Date(selectedChat.created_at).toLocaleDateString('de-DE')}
                </div>
              </div>
              <div className="flex gap-2">
                {selectedChat.status === 'open' ? (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(selectedChat, 'closed')}>
                    <XCircle size={14} /> Schliessen
                  </button>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(selectedChat, 'open')}>
                    <Clock size={14} /> Wieder öffnen
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setSelectedChat(null); setMessages([]); }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={messagesRef} style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {loadingMessages ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  Noch keine Nachrichten in diesem Chat.
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.id} style={{
                    display: 'flex',
                    justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start',
                  }}>
                    <div style={{
                      maxWidth: '75%',
                      padding: '10px 14px',
                      borderRadius: msg.sender === 'admin' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: msg.sender === 'admin'
                        ? 'linear-gradient(135deg, #c0392b, #e74c3c)'
                        : 'var(--bg-elevated)',
                      color: msg.sender === 'admin' ? '#fff' : 'var(--text)',
                      border: msg.sender === 'admin' ? 'none' : '1px solid var(--border)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}>
                      {msg.sender === 'user' && (
                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, color: 'var(--primary)' }}>
                          {selectedChat.user_email}
                        </div>
                      )}
                      {msg.sender === 'admin' && (
                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, opacity: 0.8 }}>
                          {msg.sender_email === 'system@fahryo.de' ? '🤖 Auto-Reply' : `Admin (${msg.sender_email})`}
                        </div>
                      )}
                      <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                      <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4, textAlign: msg.sender === 'admin' ? 'right' : 'left' }}>
                        {new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply input */}
            {selectedChat.status !== 'closed' && (
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border)',
                background: 'var(--bg-elevated)',
              }}>
                {replyError && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                    fontSize: 12,
                    color: '#ef4444',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span>⚠ {replyError}</span>
                    <button onClick={() => setReplyError(null)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                <textarea
                  className="input"
                  rows={2}
                  placeholder="Antwort schreiben..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }
                  }}
                  style={{ flex: 1, resize: 'none' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleReply}
                  disabled={sending || !replyText.trim()}
                  style={{ alignSelf: 'flex-end' }}
                >
                {sending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                </button>
                </div>
              </div>
            )}

            {selectedChat.status === 'closed' && (
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 13,
                background: 'var(--bg-elevated)',
              }}>
                Dieser Chat ist geschlossen.
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginLeft: 12 }}
                  onClick={() => handleStatusChange(selectedChat, 'open')}
                >
                  Wieder öffnen
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty state when no chat selected */}
        {!selectedChat && (
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
            <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
              <Headphones size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
              <p style={{ fontSize: 15, fontWeight: 600 }}>Wähle einen Chat aus</p>
              <p style={{ fontSize: 13 }}>Klicke auf einen Chat in der Liste, um die Nachrichten zu sehen.</p>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
